import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  ArrowLeft,
  MoreHorizontal,
  Pencil,
  Trash2,
  Mail,
  Printer,
  Building2,
  MapPin,
  Calendar as CalendarIcon,
  CreditCard,
  ArrowRight,
  Download
} from "lucide-react";
import api from "../lib/api";

const STATUS_LABELS = {
  draft: "Bozza",
  sent: "Inviato",
  accepted: "Accettato",
  rejected: "Rifiutato",
  expired: "Scaduto",
  converted: "Convertito",
};

export default function QuoteDetailPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuoteData();
  }, [quoteId]);

  const fetchQuoteData = async () => {
    try {
      const quoteRes = await api.get(`/quotes/${quoteId}`);
      setQuote(quoteRes.data);
      
      if (quoteRes.data.client_id) {
        const clientRes = await api.get(`/clients/${quoteRes.data.client_id}`);
        setClient(clientRes.data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento del preventivo");
      navigate("/quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success("Stato aggiornato");
      fetchQuoteData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const handleConvertToOrder = async () => {
    try {
      const res = await api.post(`/quotes/${quoteId}/convert-to-order`);
      toast.success("Preventivo convertito in ordine");
      navigate(`/orders/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella conversione");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare questo preventivo?")) return;
    try {
      await api.delete(`/quotes/${quoteId}`);
      toast.success("Preventivo eliminato");
      navigate("/quotes");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/quotes/${quoteId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Preventivo_${quote?.quote_number || quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (error) {
      toast.error("Errore nel download del PDF");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  // Formattazione speciale per contrassegno con 3 decimali
  const formatStampDuty = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  // Calculate totals from items
  const calculateTotals = () => {
    if (!quote?.items) return { imponibile: 0, iva: 0, totale: 0 };
    
    let totaleImponibile = 0;
    let totaleIva = 0;
    
    quote.items.forEach(item => {
      // Skip freebies in totals calculation
      if (item.is_freebie || item.discount === 100) return;
      
      const qty = item.quantity || 0;
      const netPrice = item.unit_price_net || item.price || 0;
      const excise = item.excise_duty || 0;
      const stamp = item.stamp_duty || 0;
      const vatRate = item.vat_rate || 22;
      
      const lineImponibile = (netPrice + excise + stamp) * qty;
      const lineIva = lineImponibile * (vatRate / 100);
      
      totaleImponibile += lineImponibile;
      totaleIva += lineIva;
    });
    
    return {
      imponibile: totaleImponibile,
      iva: totaleIva,
      totale: quote.total_amount || (totaleImponibile + totaleIva)
    };
  };

  const totals = quote ? calculateTotals() : { imponibile: 0, iva: 0, totale: 0 };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), "dd MMM yyyy", { locale: it });
    } catch {
      return "N/A";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 bg-muted rounded-xl" />
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (!quote) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/quotes")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-primary font-bold">
                Preventivo {quote.quote_number}
              </h1>
              <span className={`status-badge status-${quote.status} text-base px-3 py-1`}>
                {STATUS_LABELS[quote.status]}
              </span>
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground mt-1">
               <span className="flex items-center gap-1">
                 <CalendarIcon className="w-4 h-4" /> Emesso: {formatDate(quote.created_at)}
               </span>
               <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400 font-medium">
                 <CalendarIcon className="w-4 h-4" /> Scadenza: {formatDate(quote.valid_until)}
               </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
           {quote.status === "accepted" && (
             <Button className="bg-green-600 hover:bg-green-700" onClick={handleConvertToOrder}>
               <ArrowRight className="w-4 h-4 mr-2" />
               Converti in Ordine
             </Button>
           )}
           <Button variant="outline" onClick={handleDownloadPDF}>
            <Download className="w-4 h-4 mr-2" />
            Scarica PDF
          </Button>
           <Button variant="outline" onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" />
            Stampa
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Azioni <MoreHorizontal className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleStatusChange("sent")}>
                 Segna come Inviato
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange("accepted")}>
                 Segna come Accettato
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange("rejected")}>
                 Segna come Rifiutato
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange("expired")}>
                 Segna come Scaduto
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content: Items */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio Articoli</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prodotto</TableHead>
                    <TableHead className="text-right">Qtà</TableHead>
                    <TableHead className="text-center">U.M.</TableHead>
                    <TableHead className="text-right">Prezzo Netto</TableHead>
                    <TableHead className="text-right">Accisa/Contr.</TableHead>
                    <TableHead className="text-right">IVA</TableHead>
                    <TableHead className="text-right">Totale (IVA escl.)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.items.map((item, index) => {
                    const isFreebie = item.is_freebie || item.discount === 100;
                    return (
                    <TableRow key={index} className={isFreebie ? "bg-green-50 dark:bg-green-900/20" : ""}>
                      <TableCell>
                        <div className="font-medium flex items-center gap-2">
                          {item.name}
                          {isFreebie && <span className="text-[10px] bg-green-200 text-green-800 px-1.5 py-0.5 rounded font-bold uppercase">Omaggio</span>}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">{item.code}</div>
                        {item.promo_ref && <div className="text-[10px] text-green-600">{item.promo_ref}</div>}
                      </TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {item.unit_type === 'cartoni' ? 'Cart.' : 'Pz.'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {isFreebie ? formatCurrency(0) : formatCurrency(item.unit_price_net || 0)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">
                        {isFreebie ? "-" : ((item.excise_duty > 0 || item.stamp_duty > 0) ? (
                          <div className="flex flex-col items-end">
                            {item.excise_duty > 0 && <span>A: {formatCurrency(item.excise_duty)}</span>}
                            {item.stamp_duty > 0 && <span>C: {formatStampDuty(item.stamp_duty)}</span>}
                          </div>
                        ) : "-")}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {isFreebie ? "-" : `${item.vat_rate}%`}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {isFreebie ? formatCurrency(0) : formatCurrency(((item.unit_price_net || item.price || 0) + (item.excise_duty || 0) + (item.stamp_duty || 0)) * (item.quantity || 0))}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={5} className="text-right text-sm text-muted-foreground">Totale Imponibile</TableCell>
                    <TableCell colSpan={2} className="text-right font-mono">
                      {formatCurrency(totals.imponibile)}
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={5} className="text-right text-sm text-muted-foreground">Totale IVA</TableCell>
                    <TableCell colSpan={2} className="text-right font-mono">
                      {formatCurrency(totals.iva)}
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell colSpan={5} className="text-right font-bold">Totale Preventivo (IVA inclusa)</TableCell>
                    <TableCell colSpan={2} className="text-right text-lg text-accent font-bold">
                      {formatCurrency(totals.totale)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          
          {quote.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Note</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{quote.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar: Client Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent" />
                Dati Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="font-medium text-lg">{client?.company_name}</div>
                <div className="text-sm text-muted-foreground font-mono mt-1">{client?.client_code}</div>
              </div>
              
              <div className="space-y-2 text-sm border-t pt-3">
                <div className="flex justify-between">
                   <span className="text-muted-foreground">P.IVA:</span>
                   <span className="font-mono">{client?.vat_number}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Sede Legale:</span>
                  <div className="flex items-start gap-1 font-medium">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span>
                      {client?.address}<br/>
                      {client?.postal_code} {client?.city} ({client?.region})
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <CreditCard className="w-4 h-4" /> Condizioni
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium text-sm">Pagamento: {client?.payment_method || "Non specificato"}</p>
              <p className="text-xs text-muted-foreground mt-2">
                 * I prezzi indicati sono comprensivi di IVA e accise ove applicabile.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
